import { useGraphqlPagination } from '@components/infiniteScroll';
import { Group } from '@components/layout';
import {
  DatasetJob,
  DatasetJobFilter,
  GetJobsQuery,
  useGetDatasetJobsSearchDetailQuery,
  useGetJobsQuery,
} from '@generated/graphql';
import { useSearchDetails } from '@hooks/SearchDetails';
import { Box, useTheme } from '@mui/material';
import { useWorkspaceContext } from '@navigation/WorkspaceContext';
import { JobsManager, JobsStagedJobs } from '@subsets/workspaces';
import { useEffect } from 'react';

export const WorkspaceJobs = () => {
  const { workspaceId } = useWorkspaceContext();
  const { palette } = useTheme();

  const { activeFilters, filter, queryFilter, setFilter } = useSearchDetails<DatasetJobFilter>({
    channels: { type: 'exact', value: [], display: [] },
    dates: { type: 'date', value: [], display: [] },
    graphs: { type: 'text', value: [], display: [] },
    runs: { type: 'number', value: [], display: [] },
    tags: { type: 'string', value: [], display: [] },
    texts: { type: 'string', value: [], display: [] },
    type: { type: 'exact', value: [], display: [] },
    users: { type: 'exact', value: [], display: [] },
  });

  const { data } = useGetDatasetJobsSearchDetailQuery({
    variables: { workspaceId },
  });
  const searchDetail = data?.getDatasetJobsSearchDetail;

  const pagination = useGraphqlPagination<GetJobsQuery, DatasetJob>(
    useGetJobsQuery({
      variables: { workspaceId, limit: 15, filters: queryFilter },
    }),
    'getDatasetJobs',
    'datasetId',
    { pollInterval: 10000 },
  );

  useEffect(() => {
    if (
      pagination?.data.some((job) =>
        ['queued', 'starting', 'running', 'postprocess'].includes(job.status),
      )
    ) {
      pagination?.setPollInterval(1000);
    } else {
      pagination?.setPollInterval(10000);
    }
  }, [pagination]);

  return (
    <Group sx={{ flex: 1, height: '0', display: 'flex', gap: 6, pb: 4 }}>
      <Box flex={1} minWidth="400px" pt={6}>
        <JobsStagedJobs workspaceId={workspaceId} refetch={pagination.refetch} />
      </Box>
      <Box flex={2} pt={6} px={6} pb={4} sx={{ backgroundColor: palette.background.paper }}>
        <JobsManager
          {...pagination}
          activeFilters={activeFilters}
          filter={filter}
          setFilter={setFilter}
          searchDetail={searchDetail}
        />
      </Box>
    </Group>
  );
};
